/**
 * Generic, reusable FullCalendar wrapper (see createCalendar below).
 *
 * Project-specific behaviour is layered on through "filter controls" registered
 * via the controller. A FilterControl is a plain object; the concrete controls
 * live in index.js (subjectIsSfcFilter, serverSelectFilter). Recognised fields:
 *
 *   key            unique id used by getFilterState().
 *   paramKey       query-param name (defaults to `key`); `false` to never emit a
 *                  param for this control.
 *   serialize()    current value as a string or array of strings (or null/"").
 *   predicate(e)   optional client-side filter: keep event `e` when it returns
 *                  true. Used by filterEvents() on already-fetched events.
 *   server         when true, serialize() is also sent to the events endpoint
 *                  (server-side filtering) and changes bust the range cache.
 *   init(ctx)      called once on registration with
 *                  { controller, triggerChange } so the control can wire its UI
 *                  and call triggerChange(key) when its value changes.
 *   getState()     optional current state, exposed via getFilterState(key).
 *   serializeForIcal  set false to exclude the control from the iCal feed URL.
 */
import $ from "jquery";
import { Calendar } from "@fullcalendar/core";
import dayGridPlugin from "@fullcalendar/daygrid";
import timeGridPlugin from "@fullcalendar/timegrid";
import listPlugin from "@fullcalendar/list";

// Default view when none is supplied via options/data attribute/URL.
var DEFAULT_VIEW = "timeGridWeek";

// Base FullCalendar options; merged with the caller's `config` via deepMerge.
var DEFAULT_CONFIG = {
  plugins: [dayGridPlugin, timeGridPlugin, listPlugin],
  headerToolbar: {
    left: "prev,next today",
    center: "title",
    right: "dayGridMonth,timeGridWeek,timeGridDay,listWeek",
  },
  nowIndicator: true,
  slotMinTime: "07:00:00",
  slotMaxTime: "24:00:00",
  allDaySlot: true,
  allDayText: "",
  height: "auto",
  expandRows: true,
  slotDuration: "00:30:00",
  slotLabelInterval: "01:00:00",
  dayMaxEvents: 3,
  dayCellContent: function (arg) {
    return arg.dayNumberText.replace(".", "");
  },
  slotLabelFormat: { hour: "2-digit", minute: "2-digit", hour12: false },
  navLinks: true,
  weekNumbers: false,
  firstDay: 1,
};

// Day headers collapse to short weekday names below the Tailwind `sm` breakpoint
// (640px) so they fit narrow mobile columns; long names above it. Month view shows
// the weekday only; week/day views add the numeric day + month.
var mobileHeaderMQ = window.matchMedia("(max-width: 639px)");

function dayHeaderFormats() {
  var weekday = mobileHeaderMQ.matches ? "short" : "long";
  return {
    month: { weekday: weekday },
    dayWithDate: { weekday: weekday, day: "numeric", month: "numeric" },
  };
}

// Per-view header formats for the time-grid week/day views (the month view uses
// the global dayHeaderFormat). Shared by the initial build and the breakpoint
// listener so the two stay in sync.
function dayHeaderViews(formats) {
  return {
    timeGridWeek: { dayHeaderFormat: formats.dayWithDate },
    timeGridDay: { dayHeaderFormat: formats.dayWithDate },
  };
}

// True for plain objects (not arrays/Dates); guards deepMerge recursion.
function isPlainObject(value) {
  return (
    value &&
    typeof value === "object" &&
    !Array.isArray(value) &&
    !(value instanceof Date)
  );
}

// Recursively merge two config objects (DEFAULT_CONFIG + caller `config`).
function deepMerge(base, override) {
  var out = Array.isArray(base) ? base.slice() : Object.assign({}, base);
  Object.keys(override).forEach(function (key) {
    if (isPlainObject(out[key]) && isPlainObject(override[key])) {
      out[key] = deepMerge(out[key], override[key]);
    } else {
      out[key] = override[key];
    }
  });
  return out;
}

// Local YYYY-MM-DD (not toISOString, to avoid a UTC day shift in URL state).
function localDateStr(date) {
  return (
    date.getFullYear() +
    "-" +
    String(date.getMonth() + 1).padStart(2, "0") +
    "-" +
    String(date.getDate()).padStart(2, "0")
  );
}

// Universal server-side FilterControl for one form field — a select,
// multi-select, or text/other input. Its value is sent to the events endpoint
// (server: true): an array of selected values for a <select>, otherwise the raw
// input value (null when empty). Change is bound via jQuery so it also catches
// select2's jQuery-dispatched change, not just native events.
export function serverFieldFilter(el, paramKey) {
  function value() {
    if (el.tagName === "SELECT") {
      var values = Array.prototype.slice
        .call(el.selectedOptions)
        .map(function (option) {
          return option.value;
        })
        .filter(Boolean);
      return values.length ? values : null;
    }
    return el.value || null;
  }
  return {
    key: paramKey,
    paramKey: paramKey,
    server: true,
    serialize: value,
    getState: value,
    init: function (ctx) {
      $(el).on("change", function () {
        ctx.triggerChange(paramKey);
      });
    },
  };
}

// Serialize a "selected subset" control (a Set of active ids out of a Set of
// all ids): null when everything is selected — the common "no filter" default —
// otherwise the sorted, comma-joined ids.
export function serializeSelectedSet(active, all) {
  if (active.size === all.size) return null;
  var ids = Array.from(active).sort().join(",");
  return ids || null;
}

// Append a control's serialized value to params. Multi-value controls (e.g.
// multiple selects) serialize to an array and contribute one repeated param
// per value; scalar controls set a single param.
export function setParam(params, key, value) {
  if (value === null || value === undefined || value === "") return;
  if (Array.isArray(value)) {
    value.forEach(function (v) {
      if (v) params.append(key, v);
    });
  } else {
    params.set(key, value);
  }
}

/**
 * Generic, reusable FullCalendar wrapper.
 *
 * Owns calendar instantiation/config, event fetching with range caching, the
 * custom agenda (listWeek) view, view/date URL state, the iCal copy button and
 * locale loading. Project-specific behaviour is layered on through the filter
 * pipeline (FilterControl contract documented at the top of this file;
 * controls defined in index.js) and the returned controller API.
 *
 * @returns {object} controller with getCalendar/getElement/registerFilter/
 *   getFilterState/onFilterChange/filterEvents/refresh and a `ready` promise.
 */
export function createCalendar(el, options) {
  options = options || {};

  var eventsUrl = options.eventsUrl || el.getAttribute("data-events-url");
  var detailUrl = options.detailUrl || el.getAttribute("data-detail-url") || "";
  var defaultView = options.defaultView || DEFAULT_VIEW;
  var initialView =
    options.initialView || el.getAttribute("data-initial-view") || defaultView;
  var initialDate =
    options.initialDate || el.getAttribute("data-initial-date") || undefined;
  var lang = document.documentElement.lang || "en";
  var agendaViewType = options.agendaViewType || "listWeek";
  var icalButtonId = options.icalButtonId || "ical-copy-btn";
  var icalUrlAttr = options.icalUrlAttr || "data-ical-url";
  var noEventsText =
    options.noEventsText || el.getAttribute("data-no-events-text") || "";

  var allEvents = [];
  var cachedStart = null;
  var cachedEnd = null;
  // Signature of server-side filter params for the cached event set; changing a
  // server-side filter changes this and forces a refetch (see eventsFn).
  var cachedFilterSig = null;
  var calendar = null;
  // Registered filter controls (FilterControl contract: top of this file).
  var filters = [];
  // Subscribers notified after any filter change
  var changeHandlers = [];

  // Custom agenda container
  var agendaContainer = document.createElement("div");
  agendaContainer.className = "fc-custom-agenda hidden";
  el.parentNode.insertBefore(agendaContainer, el.nextSibling);

  var weekdayFormatter = new Intl.DateTimeFormat(lang, { weekday: "short" });

  // Apply every control's client-side `predicate` to an event array (controls
  // without a predicate — e.g. server-side filters — pass everything through).
  function filterEvents(events) {
    return events.filter(function (e) {
      return filters.every(function (control) {
        return typeof control.predicate === "function"
          ? control.predicate(e)
          : true;
      });
    });
  }

  // Render the custom listWeek agenda: bucket events per day in [start, end),
  // build the markup into agendaContainer, and wire row clicks to detailUrl.
  function renderCustomAgenda(start, end, events) {
    var dayMap = {};
    var current = new Date(start);
    while (current < end) {
      var key =
        current.getFullYear() +
        "-" +
        String(current.getMonth() + 1).padStart(2, "0") +
        "-" +
        String(current.getDate()).padStart(2, "0");
      dayMap[key] = { date: new Date(current), events: [] };
      current.setDate(current.getDate() + 1);
    }

    events.forEach(function (e) {
      var eStart = new Date(e.start);
      var eEnd = e.end
        ? new Date(e.end)
        : new Date(eStart.getTime() + 86400000);
      var from = eStart < start ? new Date(start) : new Date(eStart);
      var to = eEnd > end ? end : eEnd;
      from.setHours(0, 0, 0, 0);
      while (from < to) {
        var key =
          from.getFullYear() +
          "-" +
          String(from.getMonth() + 1).padStart(2, "0") +
          "-" +
          String(from.getDate()).padStart(2, "0");
        if (dayMap[key]) {
          dayMap[key].events.push(e);
        }
        from.setDate(from.getDate() + 1);
      }
    });

    var html = "";
    Object.keys(dayMap)
      .sort()
      .forEach(function (key) {
        var day = dayMap[key];
        if (day.events.length === 0) return;
        var d = day.date;
        var isSun = d.getDay() === 0;
        var dayNum = d.getDate();
        var weekday = weekdayFormatter.format(d);

        html +=
          '<div class="fc-agenda-day' +
          (isSun ? " fc-agenda-day-sun" : "") +
          '">';
        html += '<div class="fc-agenda-date">';
        html += '<span class="fc-agenda-date-num">' + dayNum + "</span>";
        html += '<span class="fc-agenda-date-weekday">' + weekday + "</span>";
        html += "</div>";
        html += '<div class="fc-agenda-events">';

        day.events.forEach(function (e) {
          var color = e.color || e.backgroundColor || "#6b7280";
          var eventId = e.id || "";
          var slug = (e.extendedProps && e.extendedProps.slug) || "";
          var evDetailUrl =
            (e.extendedProps && e.extendedProps.detailUrl) || "";
          html +=
            '<div class="fc-agenda-event" data-event-id="' +
            eventId +
            '" data-slug="' +
            slug +
            '" data-detail-url="' +
            evDetailUrl +
            '">';
          html +=
            '<span class="fc-agenda-dot" style="background:' +
            color +
            '"></span>';
          html +=
            '<span class="fc-agenda-event-title">' +
            (e.title || "") +
            "</span>";
          html += "</div>";
        });

        html += "</div></div>";
      });

    if (html === "") {
      html = '<div class="fc-agenda-empty">' + noEventsText + "</div>";
    }

    agendaContainer.innerHTML = html;

    agendaContainer
      .querySelectorAll(".fc-agenda-event")
      .forEach(function (node) {
        node.addEventListener("click", function () {
          var dUrl = node.getAttribute("data-detail-url");
          if (dUrl) {
            window.location.href = dUrl;
          }
        });
      });
  }

  // Re-render the custom agenda only when that view is currently shown.
  function refreshAgendaIfActive() {
    if (calendar && calendar.view.type === agendaViewType) {
      var view = calendar.view;
      renderCustomAgenda(
        view.activeStart,
        view.activeEnd,
        filterEvents(allEvents),
      );
    }
  }

  // URLSearchParams of every registered control's value, used for URL state, the
  // iCal button URL and the iCal feed POST. `forIcal` drops controls that opt
  // out of the feed (serializeForIcal === false).
  function filterParams(opts) {
    opts = opts || {};
    var params = new URLSearchParams();
    filters.forEach(function (control) {
      if (control.paramKey === false) return;
      if (opts.forIcal && control.serializeForIcal === false) return;
      var value =
        typeof control.serialize === "function" ? control.serialize() : null;
      setParam(params, control.paramKey || control.key, value);
    });
    return params;
  }

  // Rewrite the iCal button's URL attribute query string to mirror the active
  // filters (so the copied feed link reflects what is on screen).
  function rebuildIcalUrl() {
    var icalBtn = document.getElementById(icalButtonId);
    if (!icalBtn) return;

    var icalParams = filterParams({ forIcal: true });

    var icalParts = [];
    icalParams.forEach(function (value, key) {
      icalParts.push(key + "=" + value);
    });
    var icalQs = icalParts.join("&");
    var origin = window.location.origin;
    var basePath = icalBtn
      .getAttribute(icalUrlAttr)
      .replace(/\?.*$/, "")
      .replace(origin, "");
    icalBtn.setAttribute(
      icalUrlAttr,
      origin + basePath + (icalQs ? "?" + icalQs : ""),
    );
  }

  // Reflect current view, date and filter values into the address bar via
  // history.replaceState (no navigation), then resync the iCal button URL.
  function pushStateToUrl() {
    if (!calendar) return;
    var params = new URLSearchParams();

    // View: omit if default
    var currentView = calendar.view.type;
    if (currentView !== defaultView) {
      params.set("view", currentView);
    }

    // Date: omit if today (use local date to avoid UTC shift)
    var currentDateStr = localDateStr(calendar.getDate());
    var todayStr = localDateStr(new Date());
    if (currentDateStr !== todayStr) {
      params.set("date", currentDateStr);
    }

    // Each registered control contributes its own param
    filterParams().forEach(function (value, key) {
      params.append(key, value);
    });

    var parts = [];
    params.forEach(function (value, key) {
      parts.push(key + "=" + value);
    });
    var qs = parts.join("&");
    var newUrl = window.location.pathname + (qs ? "?" + qs : "");
    history.replaceState(null, "", newUrl);

    // Keep the iCal button URL in sync with the active filters
    rebuildIcalUrl();
  }

  // Fan out a filter-change to every onFilterChange subscriber.
  function notify(changedKey) {
    changeHandlers.forEach(function (handler) {
      handler(changedKey);
    });
  }

  // Refetch events (re-runs eventsFn), re-render the agenda, sync the URL.
  function refresh() {
    if (calendar) calendar.refetchEvents();
    refreshAgendaIfActive();
    pushStateToUrl();
  }

  // A control calls this (via init's ctx) after its value changes: notify
  // subscribers, then refetch/redraw.
  function triggerChange(changedKey) {
    notify(changedKey);
    refresh();
  }

  // Register a FilterControl and run its init() so it can wire its UI.
  function registerFilter(control) {
    filters.push(control);
    if (typeof control.init === "function") {
      control.init({ controller: controller, triggerChange: triggerChange });
    }
  }

  // Read a registered control's current state by its `key` (null if none).
  function getFilterState(key) {
    for (var i = 0; i < filters.length; i++) {
      if (filters[i].key === key && typeof filters[i].getState === "function") {
        return filters[i].getState();
      }
    }
    return null;
  }

  // Subscribe to filter changes; returns an unsubscribe function.
  function onFilterChange(handler) {
    changeHandlers.push(handler);
    return function () {
      var idx = changeHandlers.indexOf(handler);
      if (idx !== -1) changeHandlers.splice(idx, 1);
    };
  }

  // Wire the iCal button to copy its current URL to the clipboard (with an
  // execCommand fallback) and show transient feedback. Skipped if no button.
  function initIcalButton() {
    var icalCopyBtn = document.getElementById(icalButtonId);
    if (!icalCopyBtn) return;

    icalCopyBtn.addEventListener("click", function () {
      var url = icalCopyBtn.getAttribute(icalUrlAttr);
      var label = icalCopyBtn.querySelector(".ical-label");
      var original = label ? label.textContent : "";

      function showFeedback(text) {
        if (!label) return;
        label.textContent = text;
        setTimeout(function () {
          label.textContent = original;
        }, 2000);
      }

      function fallbackCopy(text) {
        var textarea = document.createElement("textarea");
        textarea.value = text;
        textarea.style.position = "fixed";
        textarea.style.opacity = "0";
        document.body.appendChild(textarea);
        textarea.select();
        try {
          document.execCommand("copy");
          showFeedback("✓ Copied!");
        } catch (e) {
          showFeedback("Failed to copy");
        }
        document.body.removeChild(textarea);
      }

      if (navigator.clipboard && navigator.clipboard.writeText) {
        navigator.clipboard.writeText(url).then(
          function () {
            showFeedback("✓ Copied!");
          },
          function () {
            fallbackCopy(url);
          },
        );
      } else {
        fallbackCopy(url);
      }
    });
  }

  // Params for controls that filter server-side (control.server === true).
  // These are sent to the events endpoint, unlike client-side predicate filters.
  function serverFilterParams() {
    var params = new URLSearchParams();
    filters.forEach(function (control) {
      if (!control.server || control.paramKey === false) return;
      var value =
        typeof control.serialize === "function" ? control.serialize() : null;
      setParam(params, control.paramKey || control.key, value);
    });
    return params;
  }

  // FullCalendar event source. Fetches the visible range with the server-side
  // filter params appended, caching the result by range + filter signature; on
  // a cache hit it re-serves the cached events through the client predicates.
  function eventsFn(info, successCallback, failureCallback) {
    var serverParams = serverFilterParams();
    var serverSig = serverParams.toString();
    if (
      cachedStart === info.startStr &&
      cachedEnd === info.endStr &&
      cachedFilterSig === serverSig
    ) {
      successCallback(filterEvents(allEvents));
      return;
    }
    var params = new URLSearchParams({
      start: info.startStr,
      end: info.endStr,
    });
    serverParams.forEach(function (value, key) {
      params.append(key, value);
    });
    fetch(eventsUrl + "?" + params.toString())
      .then(function (r) {
        if (!r.ok) throw new Error("HTTP " + r.status);
        return r.json();
      })
      .then(function (data) {
        allEvents = data;
        cachedStart = info.startStr;
        cachedEnd = info.endStr;
        cachedFilterSig = serverSig;
        successCallback(filterEvents(data));
        refreshAgendaIfActive();
      })
      .catch(failureCallback);
  }

  // FullCalendar datesSet callback (view/date changed): swap between the custom
  // agenda and FC's own harness, render the agenda if active, and sync the URL.
  function datesSetFn(info) {
    var isListView = info.view.type === agendaViewType;
    agendaContainer.classList.toggle("hidden", !isListView);
    var harness = el.querySelector(".fc-view-harness");
    if (harness) harness.style.display = isListView ? "none" : "";
    if (isListView) {
      renderCustomAgenda(
        info.view.activeStart,
        info.view.activeEnd,
        filterEvents(allEvents),
      );
    }
    pushStateToUrl();
  }

  // FullCalendar eventClick: navigate to the event's detailUrl, if any.
  function eventClickFn(info) {
    var dUrl =
      info.event.extendedProps.detailUrl ||
      info.el.getAttribute("data-detail-url");
    if (dUrl) {
      info.jsEvent.preventDefault();
      window.location.href = dUrl;
    }
  }

  // FullCalendar eventDidMount: mark events clickable and cache detailUrl on the
  // DOM node (used by eventClickFn's fallback).
  function eventDidMountFn(info) {
    info.el.style.cursor = "pointer";
    var dUrl = info.event.extendedProps.detailUrl;
    if (dUrl) {
      info.el.setAttribute("data-detail-url", dUrl);
    }
  }

  // Merge default + caller config and wire the FC callbacks/locale into the
  // final options object passed to `new Calendar(...)`.
  function buildCalendarOpts(localeData) {
    var opts = deepMerge(DEFAULT_CONFIG, options.config || {});
    // Global format drives the month view; week/day views override it with the
    // numeric date appended.
    var formats = dayHeaderFormats();
    opts.dayHeaderFormat = formats.month;
    opts.views = deepMerge(opts.views || {}, dayHeaderViews(formats));
    opts.initialView = initialView;
    opts.events = eventsFn;
    opts.datesSet = datesSetFn;
    opts.eventClick = eventClickFn;
    opts.eventDidMount = eventDidMountFn;
    if (initialDate) {
      opts.initialDate = initialDate;
    }
    if (localeData) {
      opts.locale = localeData;
    }
    return opts;
  }

  // Public API returned to callers (and passed to control.init as `controller`).
  // `controller.ready` (set below) resolves once the calendar is rendered.
  var controller = {
    getCalendar: function () {
      return calendar;
    },
    getElement: function () {
      return el;
    },
    registerFilter: registerFilter,
    getFilterState: getFilterState,
    onFilterChange: onFilterChange,
    filterEvents: filterEvents,
    refresh: refresh,
    // Active filter params (for the iCal feed POST body / external callers).
    getFilterParams: function () {
      return filterParams({ forIcal: true });
    },
  };

  // detailUrl is preserved for consumers that derive event URLs from it,
  // matching the original data-detail-url fallback used in eventClick.
  controller.detailUrl = detailUrl;

  // Register a server-side control for every named, non-hidden field of the
  // filter form (resolved from options.filterForm or the element's
  // data-filter-form selector). Skips FiltersetForm's hidden _search and
  // select2's nameless internal search input.
  function registerFormFilters(formEl) {
    formEl
      .querySelectorAll("select[name], input[name], textarea[name]")
      .forEach(function (field) {
        if (field.type === "hidden") return;
        registerFilter(serverFieldFilter(field, field.name));
      });
  }

  initIcalButton();

  (options.filters || []).forEach(registerFilter);

  var filterFormSel = options.filterForm || el.getAttribute("data-filter-form");
  var filterFormEl =
    typeof filterFormSel === "string"
      ? document.querySelector(filterFormSel)
      : filterFormSel;
  if (filterFormEl) registerFormFilters(filterFormEl);

  // Conditionally load the Slovenian locale based on the html lang attribute,
  // then build and render the calendar. Filters are registered synchronously
  // above so wrappers can read state immediately; getCalendar() stays null
  // until this resolves.
  var localePromise =
    lang === "sl"
      ? import(
          /* webpackInclude: /sl\.js$/ */
          "@fullcalendar/core/locales/sl"
        ).then(function (mod) {
          return mod.default;
        })
      : Promise.resolve(null);

  controller.ready = localePromise.then(function (localeData) {
    calendar = new Calendar(el, buildCalendarOpts(localeData));
    calendar.render();
    mobileHeaderMQ.addEventListener("change", function () {
      var formats = dayHeaderFormats();
      calendar.batchRendering(function () {
        calendar.setOption("dayHeaderFormat", formats.month);
        calendar.setOption("views", dayHeaderViews(formats));
      });
    });
    if (typeof options.onReady === "function") {
      options.onReady(controller);
    }
    return controller;
  });

  return controller;
}
